"use client"

import { useEffect, useRef, useState } from "react"
import mermaid from "mermaid"
import { Maximize2, X, Download, ZoomIn, ZoomOut, NotebookPen, Check } from "lucide-react"
import { postAws } from "../aws"

const FlowChart = ({ message, book }) => {
  const mermaidRef = useRef(null)
  const containerRef = useRef(null)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaved, setIsSaved] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [renderError, setRenderError] = useState(null)
  const [showRawText, setShowRawText] = useState(false)

  // Parse the flowchart data from the message
  const parseFlowchartData = () => {
    try {
      if (message.flowchart) {
        const mermaidMatch = message.flowchart.match(/```mermaid\s*([\s\S]*?)\s*```/)
        return mermaidMatch ? mermaidMatch[1].trim() : message.flowchart
      }
      if (typeof message.message === "string") {
        const parsedMessage = JSON.parse(message.message)
        if (parsedMessage.flowchart) {
          const mermaidMatch = parsedMessage.flowchart.match(/```mermaid\s*([\s\S]*?)\s*```/)
          return mermaidMatch ? mermaidMatch[1].trim() : parsedMessage.flowchart
        }
      }
      return null
    } catch (error) {
      console.error("Error parsing flowchart data:", error)
      return null
    }
  }

  const flowchartDefinition = parseFlowchartData()

  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen()
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen()
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
    setIsFullScreen(!isFullScreen)
  }

  const downloadFlowChart = () => {
    if (mermaidRef.current) {
      const svgElement = mermaidRef.current.querySelector("svg")
      if (svgElement) {
        const svgData = new XMLSerializer().serializeToString(svgElement)
        const blob = new Blob([svgData], { type: "image/svg+xml" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = "flowchart.svg"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    }
  }

  const zoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.1, 2))
  const zoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5))

  const handleSaveToNotes = async () => {
    const notesData = {
      classRoomId: localStorage.getItem("classRoomId"),
      noteType: "flowchart",
      noteTitle: `${book.fileTitle} Flowchart`,
      refTitle: book.fileTitle,
      refId: book._id,
      userId: localStorage.getItem("teacherId"),
      noteContent: JSON.stringify(message),
      mediaUrl: "",
    }

    const saveNote = await postAws("https://api-dev.aurait.ai/notes", "POST", notesData)
    if (saveNote.statusCode === 201) {
      setIsSaved(true)
    }
  }

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        !!(
          document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        ),
      )
    }

    document.addEventListener("fullscreenchange", handleFullScreenChange)
    document.addEventListener("mozfullscreenchange", handleFullScreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange)
    document.addEventListener("MSFullscreenChange", handleFullScreenChange)

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange)
      document.removeEventListener("mozfullscreenchange", handleFullScreenChange)
      document.removeEventListener("webkitfullscreenchange", handleFullScreenChange)
      document.removeEventListener("MSFullscreenChange", handleFullScreenChange)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768
      if (isMobile) {
        setZoomLevel(isFullScreen ? 1 : 0.8)
      } else {
        setZoomLevel(1)
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [isFullScreen])

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      theme: "base",
      themeVariables: {
        primaryColor: "#3b82f6",
        primaryTextColor: "#ffffff",
        primaryBorderColor: "#2563eb",
        lineColor: "#64748b",
        secondaryColor: "#10b981",
        tertiaryColor: "#f59e0b",
      },
      flowchart: {
        curve: "basis",
        padding: 8,
        useMaxWidth: true,
        htmlLabels: true,
        diagramPadding: 4,
        nodeSpacing: 20,
        rankSpacing: 20,
      },
      securityLevel: "loose",
    })

    if (mermaidRef.current && flowchartDefinition && !showRawText) {
      setIsLoading(true)
      setRenderError(null)
      const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`

      try {
        // Validate the flowchart syntax before rendering
        const lines = flowchartDefinition.split("\n")
        const definedNodes = new Set()
        const referencedNodes = new Set()

        // Extract defined and referenced nodes
        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine || trimmedLine.startsWith("flowchart")) continue

          // Find node definitions
          const nodeMatches = trimmedLine.match(/([A-Z]+)[[{]/g)
          if (nodeMatches) {
            nodeMatches.forEach((match) => {
              const nodeId = match.replace(/[[{]/, "")
              definedNodes.add(nodeId)
            })
          }

          // Find node references in connections
          if (trimmedLine.includes("-->")) {
            const parts = trimmedLine.split("-->")
            parts.forEach((part) => {
              const cleanPart = part.trim().replace(/\|[^|]*\|/g, "")
              const nodeMatch = cleanPart.match(/^([A-Z]+)/)
              if (nodeMatch) {
                referencedNodes.add(nodeMatch[1])
              }
            })
          }
        }

        // Check for undefined node references
        const undefinedNodes = [...referencedNodes].filter((node) => !definedNodes.has(node))
        if (undefinedNodes.length > 0) {
          throw new Error(`Undefined nodes referenced: ${undefinedNodes.join(", ")}`)
        }

        mermaid
          .render(id, flowchartDefinition)
          .then(({ svg }) => {
            if (mermaidRef.current) {
              mermaidRef.current.innerHTML = svg
              const svgElement = mermaidRef.current.querySelector("svg")
              if (svgElement) {
                // Make SVG responsive
                svgElement.setAttribute("width", "100%")
                svgElement.setAttribute("height", "100%")

                // Calculate and set viewBox
                const bbox = svgElement.getBBox()
                const padding = 20
                svgElement.setAttribute(
                  "viewBox",
                  `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`,
                )

                // Ensure proper scaling
                svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet")

                // Apply styles to nodes
                const nodes = svgElement.querySelectorAll(".node")
                nodes.forEach((node) => {
                  const shape = node.querySelector("rect, circle, ellipse, polygon")
                  const text = node.querySelector("text")

                  if (shape) {
                    shape.setAttribute("fill", "#3b82f6")
                    shape.setAttribute("stroke", "#2563eb")
                    shape.setAttribute("stroke-width", "1.5")
                    shape.setAttribute("rx", "4")
                    shape.setAttribute("ry", "4")
                  }

                  if (text) {
                    text.setAttribute("fill", "#ffffff")
                    text.style.fontSize = "12px"
                    text.style.fontWeight = "500"
                  }
                })

                // Style edges
                const edges = svgElement.querySelectorAll(".edgePath")
                edges.forEach((edge) => {
                  const path = edge.querySelector("path")
                  if (path) {
                    path.setAttribute("stroke", "#64748b")
                    path.setAttribute("stroke-width", "1.5")
                  }
                })

                // Add enhanced CSS styles
                const styleElement = document.createElementNS("http://www.w3.org/2000/svg", "style")
                styleElement.textContent = `
                .node rect { filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1)); }
                .node:hover rect { filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2)); }
                .edgePath path { transition: stroke-width 0.2s; }
                .edgePath:hover path { stroke-width: 2px; }
              `
                svgElement.appendChild(styleElement)
              }
              setIsLoading(false)
              setRenderError(null)
            }
          })
          .catch((error) => {
            console.error("Mermaid rendering failed:", error)
            setRenderError(error.message)
            setIsLoading(false)
          })
      } catch (error) {
        console.error("Flowchart validation failed:", error)
        setRenderError(error.message)
        setIsLoading(false)
      }
    }
  }, [flowchartDefinition, isFullScreen, showRawText])

  if (!flowchartDefinition) {
    return (
      <div className="p-5 bg-red-50 text-red-700 rounded-lg border border-red-200 shadow-sm">
        <p className="font-medium">No valid flowchart data found</p>
        <p className="text-sm mt-1">Please check the format of your flowchart data and try again.</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`relative bg-white rounded-lg shadow-sm transition-all duration-300 ${
        isFullScreen ? "fixed inset-0 z-[9999] w-screen h-screen" : "w-full mx-auto"
      }`}
    >
      <div className="flex justify-between items-center p-2 border-b">
        <h3 className="text-lg font-semibold text-blue-700 flex items-center gap-2">Flow Diagram</h3>
        <div className="flex items-center gap-1">
     

          {renderError && (
            <button
              onClick={() => setShowRawText(!showRawText)}
              className="p-1.5 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white transition-colors"
              title={showRawText ? "Show Diagram" : "Show Raw Text"}
            >
              {showRawText ? "📊" : "📝"}
            </button>
          )}

          <button
            onClick={zoomOut}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={zoomIn}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={downloadFlowChart}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Download flowchart"
            disabled={renderError && !showRawText}
          >
            <Download className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={toggleFullScreen}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors mr-8"
            aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
          >
            {isFullScreen ? <X className="h-4 w-4 text-gray-600" /> : <Maximize2 className="h-4 w-4 text-gray-600" />}
          </button>
        </div>
      </div>

      <div className="relative bg-white">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
            <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

        {renderError && !showRawText && (
          <div className="p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 m-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium">Flowchart Rendering Error</h3>
                <p className="text-sm mt-1">{renderError}</p>
                <button
                  onClick={() => setShowRawText(true)}
                  className="mt-2 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded-md transition-colors"
                >
                  Show Raw Text Instead
                </button>
              </div>
            </div>
          </div>
        )}

        {showRawText ? (
          <div className="p-4 bg-gray-50 rounded-lg m-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-gray-700">Raw Flowchart Definition:</h4>
              <button
                onClick={() => setShowRawText(false)}
                className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-md transition-colors"
              >
                Try Rendering Again
              </button>
            </div>
            <pre className="text-sm text-gray-600 whitespace-pre-wrap font-mono bg-white p-3 rounded border overflow-auto max-h-96">
              {flowchartDefinition}
            </pre>
          </div>
        ) : (
          <div
            ref={mermaidRef}
            className="mermaid-container p-4 min-h-[200px] max-h-[400px] overflow-auto"
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: "center center",
              transition: "transform 0.2s ease-out",
            }}
          >
            {!renderError && flowchartDefinition}
          </div>
        )}
      </div>

      {isFullScreen && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-white rounded-full shadow p-2">
          <button
            onClick={zoomOut}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4 text-gray-600" />
          </button>
          <span className="text-sm font-medium px-2">{Math.round(zoomLevel * 100)}%</span>
          <button
            onClick={zoomIn}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  )
}

export default FlowChart